import { isRequestError } from "@prismicio/editor-support/Http"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Alert,
  AnimatedElement,
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogTitle,
  Form,
  FormInput,
  PrismicLogo,
  Text,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { emailLogin } from "../services/auth"
import { setDevToken } from "./devToken"

interface Props {
  shown: boolean
  onLoggedIn: () => void
}

export function LoginForm(props: Props) {
  const { shown, onLoggedIn } = props

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<number>()

  // Reset the form when it's shown again.
  // We do it here instead of right before the Form is hidden because
  // it does not feel good to see the submit button being disabled,
  // then enabled briefly while it animates out.
  // Doing this on animationEnd + show=false would be better but: https://github.com/radix-ui/primitives/issues/1020
  useOnChange(shown, () => {
    if (shown) setSubmitting(false)
  })

  function onSubmit() {
    setSubmitting(true)

    emailLogin({ email, password })
      .then((token) => {
        setDevToken(token)
        onLoggedIn()
      })
      .catch((err: unknown) => {
        setSubmitting(false)

        const status =
          isRequestError(err) && err.detail.type === "http"
            ? err.detail.status
            : 500

        setFormError(status)
      })
  }

  return (
    <Dialog size="fullscreen" open={shown}>
      <Box
        width="100%"
        height="100%"
        flexDirection="column"
        alignItems="center"
      >
        <Box padding={{ top: 60, bottom: 24 }}>
          <PrismicLogo />
        </Box>

        <DialogTitle hidden>Log in</DialogTitle>

        <DialogDescription asChild>
          <Text variant="h1">Log in to your account</Text>
        </DialogDescription>

        <Box padding={{ top: 4 }}>
          <Text variant="bold" color="grey11">
            Don't have an account?
          </Text>
        </Box>

        <Box width={360} flexDirection="column">
          <FormError formError={formError} />

          <Box flexDirection="column" padding={{ top: 32 }}>
            <Form onSubmit={onSubmit}>
              <FormInput
                type="email"
                label="Email"
                autoComplete="username"
                value={email}
                onValueChange={setEmail}
              />

              <Box gap={8} flexDirection="column" padding={{ bottom: 8 }}>
                <FormInput
                  type="password"
                  label="Password"
                  autoComplete="current-password"
                  value={password}
                  onValueChange={setPassword}
                />
                <Text variant="small" color="grey11">
                  This site is protected by reCAPTCHA and the Google Privacy
                  Policy and Terms of Service apply.
                </Text>
              </Box>
              <Button type="submit" size="large" disabled={submitting}>
                Sign in
              </Button>
            </Form>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}

function FormError(props: { formError?: number }) {
  const { formError } = props

  const alert = formError && (
    <Box key={formError} padding={{ top: 24 }} flexDirection="column">
      <Alert
        icon="alert"
        title={
          formError === 401
            ? "Incorrect username or password."
            : "We had a technical issue."
        }
      />
    </Box>
  )

  return <AnimatedElement>{alert}</AnimatedElement>
}
